import React from 'react';
import {
  Accordion as MuiAccordion,
  AccordionSummary as MuiAccordionSummary,
  AccordionDetails as MuiAccordionDetails,
  Typography,
  Box,
  useTheme,
  useMediaQuery
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useAccordionState } from './hooks';
import { getContentPadding, getMinHeight } from './utils';
import type { AccordionProps } from './types';

const Accordion: React.FC<AccordionProps> = ({
  title,
  subtitle,
  children,
  expanded: controlledExpanded,
  defaultExpanded = false,
  onChange,
  elevation = 1,
  headerSx,
  contentSx,
  sx,
  noPadding = false,
  disabled = false,
  disableGutters = false,
  square = false,
  expandIcon = <ExpandMoreIcon />,
  headerActions,
  TransitionProps,
  keepMounted = false,
  id,
  'aria-controls': ariaControls,
  ...otherProps
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery('(max-width: 1023px)');
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const { isExpanded, handleChange } = useAccordionState(
    controlledExpanded,
    defaultExpanded,
    onChange
  );

  const contentPadding = getContentPadding(noPadding, isMobile, isTablet);
  const minHeight = getMinHeight(isMobile);

  return (
    <MuiAccordion
      expanded={isExpanded}
      onChange={handleChange}
      disabled={disabled}
      disableGutters={disableGutters}
      square={square}
      elevation={elevation}
      TransitionProps={{
        ...TransitionProps,
        unmountOnExit: !keepMounted
      }}
      id={id}
      sx={{
        width: '100%',
        '&:before': {
          display: 'none'
        },
        '&.Mui-expanded': {
          margin: disableGutters ? 0 : 'auto'
        },
        ...sx
      }}
      {...otherProps}
    >
      <MuiAccordionSummary
        expandIcon={!disabled ? expandIcon : null}
        aria-controls={ariaControls || `${id}-content`}
        id={`${id}-header`}
        sx={{
          backgroundColor:
            theme.palette.mode === 'dark'
              ? 'rgba(255, 255, 255, 0.05)'
              : theme.palette.grey[50],
          minHeight,
          '&.Mui-expanded': {
            minHeight
          },
          '& .MuiAccordionSummary-content': {
            margin: '12px 0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%'
          },
          '& .MuiAccordionSummary-expandIconWrapper': {
            order: 1,
            marginLeft: 'auto',
            marginRight: 0
          },
          '&.Mui-disabled': {
            opacity: 0.6
          },
          ...headerSx
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
          {typeof title === 'string' ? (
            <Typography
              variant={isMobile ? 'body1' : 'subtitle1'}
              sx={{
                fontWeight: 'medium',
                color:
                  theme.palette.mode === 'dark'
                    ? theme.palette.primary.main
                    : theme.palette.text.primary
              }}
            >
              {title}
            </Typography>
          ) : (
            title
          )}

          {subtitle &&
            (typeof subtitle === 'string' ? (
              <Typography
                variant="caption"
                sx={{
                  color: theme.palette.text.secondary,
                  mt: 0.5
                }}
              >
                {subtitle}
              </Typography>
            ) : (
              subtitle
            ))}
        </Box>

        {headerActions && (
          <Box
            sx={{
              ml: 2,
              display: 'flex',
              alignItems: 'center'
            }}
          >
            {React.Children.map(headerActions, child =>
              React.isValidElement(child)
                ? React.cloneElement(
                    child as React.ReactElement<any>,
                    {
                      onClick: (e: React.MouseEvent) => {
                        e.stopPropagation();
                        if (
                          typeof (child as any).props?.onClick === 'function'
                        ) {
                          (child as any).props.onClick(e);
                        }
                      }
                    } as any
                  )
                : child
            )}
          </Box>
        )}
      </MuiAccordionSummary>

      <MuiAccordionDetails
        sx={{
          pt: contentPadding,
          pb: contentPadding,
          px: contentPadding,
          backgroundColor:
            theme.palette.mode === 'dark' ? '#000000' : 'inherit',
          ...contentSx
        }}
      >
        {children}
      </MuiAccordionDetails>
    </MuiAccordion>
  );
};

export default Accordion;
